import {
  ApolloClient,
  ApolloLink,
  split,
  concat,
  ServerParseError,
  ServerError,
} from "@apollo/client";
import { InMemoryCache } from "@apollo/client/cache";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { WebSocketLink } from "@apollo/client/link/ws";
import { getMainDefinition } from "@apollo/client/utilities";
import omitDeep from "@wertarbyte/omit-deep";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { createUploadLink } from "apollo-upload-client";
import { OperationDefinitionNode } from "graphql";
import JWT from "jwt-client";
import { SubscriptionClient } from "subscriptions-transport-ws";

import runtimeConfig from "../runtimeConfig";

import customFetch from "./customFetch";

export const serverUrl =
  localStorage.getItem("serverUrl") ||
  runtimeConfig.serverUrl ||
  "http://localhost:3010";

const authLink = setContext((_, { headers }) => {
  const token = JWT.get();
  return {
    headers: {
      ...headers,
      Authorization: token ?? "",
    },
  };
});

const cleanTypenameLink = new ApolloLink((operation, forward) => {
  const keysToOmit = ["__typename"]; // more keys like timestamps could be included here

  const definition = getMainDefinition(operation.query);
  if ((definition as OperationDefinitionNode)?.operation === "mutation") {
    operation.variables = omitDeep(operation.variables, keysToOmit, false);
  }
  return forward ? forward(operation) : null;
});

const uploadLink = createUploadLink({
  uri: `${serverUrl}/graphql`,
  fetch: customFetch,
});

const webSocketClient: SubscriptionClient = new SubscriptionClient(
  `ws${serverUrl.slice(4)}/graphql`,
  {
    reconnect: true,
    connectionParams: () => {
      const token = JWT.get();
      return { Authorization: token ?? "" };
    },
  }
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const webSocketLink: any = new WebSocketLink(webSocketClient);

const cache = new InMemoryCache({ addTypename: false });

export default new ApolloClient({
  /* eslint-disable no-console */
  link: ApolloLink.from([
    cleanTypenameLink,
    onError(({ networkError, graphQLErrors }) => {
      if (
        (networkError as ServerError | ServerParseError)?.statusCode === 401 ||
        graphQLErrors?.some((e) => e.extensions?.exception?.status === 401)
      ) {
        if (window.location.pathname !== "/auth/login") {
          window.location.href = "/auth/login";
        }
      }
    }),
    onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors) {
        graphQLErrors.map((error) => console.error("GraphQL error", error));
      }
      if (networkError) console.error("Network error", networkError);
    }),
    split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === "OperationDefinition" &&
          definition.operation === "subscription"
        );
      },
      webSocketLink,
      concat(authLink, uploadLink)
    ),
  ]),
  cache,
});

export const disconnectSubscriptionClient = (): void => {
  webSocketLink.subscriptionClient.close(true, true);
};

export const reconnectSubscriptionClient = (): void => {
  disconnectSubscriptionClient();
  webSocketLink.subscriptionClient.connect();
};
